import React, { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const slug = (s) => s.replace(/\s+/g, "-").toLowerCase();

// Agrupa una lista en: Mes en curso · Meses anteriores (año actual) · Años anteriores (por año y mes) · Sin fecha.
export default function AgrupadoPorFecha({ rows, getFecha, renderRows, resumen, emptyText = "No hay registros en este periodo.", testidPrefix = "grp" }) {
  const g = useMemo(() => {
    const now = new Date();
    const curY = now.getFullYear(), curM = now.getMonth();
    const sortDesc = (a, b) => (getFecha(b) || "").localeCompare(getFecha(a) || "");
    const mesEnCurso = [], mesesAnio = {}, porAnio = {}, sinFecha = [];
    rows.forEach((r) => {
      const f = getFecha(r);
      const d = f ? new Date(f + "T00:00:00") : null;
      if (!d || isNaN(d)) { sinFecha.push(r); return; }
      const y = d.getFullYear(), m = d.getMonth();
      if (y === curY && m === curM) mesEnCurso.push(r);
      else if (y >= curY) (mesesAnio[m] = mesesAnio[m] || []).push(r);
      else { porAnio[y] = porAnio[y] || {}; (porAnio[y][m] = porAnio[y][m] || []).push(r); }
    });
    mesEnCurso.sort(sortDesc); sinFecha.sort(sortDesc);
    Object.values(mesesAnio).forEach((a) => a.sort(sortDesc));
    Object.values(porAnio).forEach((mm) => Object.values(mm).forEach((a) => a.sort(sortDesc)));
    return { mesEnCurso, mesesAnio, porAnio, sinFecha, curY };
  }, [rows, getFecha]);

  const mesesKeys = Object.keys(g.mesesAnio).map(Number).sort((a, b) => a - b);
  const mesesAll = mesesKeys.flatMap((m) => g.mesesAnio[m]);
  const aniosKeys = Object.keys(g.porAnio).sort((a, b) => b.localeCompare(a));
  const aniosAll = aniosKeys.flatMap((y) => Object.values(g.porAnio[y]).flat());

  const Head = ({ titulo, list, big }) => (
    <span className="flex flex-wrap items-center gap-2">
      <span className={big ? "text-lg font-semibold" : "font-semibold"}>{titulo}</span>
      <Badge variant="secondary">{list.length}</Badge>
      {resumen && list.length > 0 && <span className="text-sm text-muted-foreground font-normal">{resumen(list)}</span>}
    </span>
  );
  const Seccion = ({ titulo, list }) => (
    <div className="space-y-2" data-testid={`${testidPrefix}-seccion-${slug(titulo)}`}>
      <Head titulo={titulo} list={list} big />
      {list.length === 0 ? <p className="text-sm text-muted-foreground pb-2">{emptyText}</p> : renderRows(list)}
    </div>
  );

  return (
    <div className="space-y-6">
      <Seccion titulo="Mes en curso" list={g.mesEnCurso} />
      <Accordion type="multiple" className="border rounded-md divide-y">
        <AccordionItem value="meses-anio" className="border-0 px-4">
          <AccordionTrigger data-testid={`${testidPrefix}-acc-meses-anteriores`} className="hover:no-underline">
            <Head titulo={`Meses anteriores (${g.curY})`} list={mesesAll} big />
          </AccordionTrigger>
          <AccordionContent className="space-y-6 pt-2">
            {mesesKeys.length === 0
              ? <p className="text-sm text-muted-foreground">No hay registros en meses anteriores de este año.</p>
              : mesesKeys.map((m) => <Seccion key={m} titulo={`${MESES[m]} ${g.curY}`} list={g.mesesAnio[m]} />)}
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="anios" className="border-0 px-4">
          <AccordionTrigger data-testid={`${testidPrefix}-acc-anios-anteriores`} className="hover:no-underline">
            <Head titulo="Años anteriores" list={aniosAll} big />
          </AccordionTrigger>
          <AccordionContent className="pt-2">
            {aniosKeys.length === 0 ? <p className="text-sm text-muted-foreground">No hay registros de años anteriores.</p> : (
              <Accordion type="multiple" className="border rounded-md divide-y">
                {aniosKeys.map((y) => {
                  const mk = Object.keys(g.porAnio[y]).map(Number).sort((a, b) => a - b);
                  return (
                    <AccordionItem key={y} value={y} className="border-0 px-4">
                      <AccordionTrigger data-testid={`${testidPrefix}-acc-anio-${y}`} className="hover:no-underline">
                        <Head titulo={y} list={Object.values(g.porAnio[y]).flat()} />
                      </AccordionTrigger>
                      <AccordionContent className="space-y-6 pt-2">
                        {mk.map((m) => <Seccion key={m} titulo={`${MESES[m]} ${y}`} list={g.porAnio[y][m]} />)}
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      {g.sinFecha.length > 0 && <Seccion titulo="Sin fecha" list={g.sinFecha} />}
    </div>
  );
}
